/* eslint-disable max-lines */
/* eslint-disable id-length */
/* eslint-disable operator-linebreak */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import { TableUtility } from '@utility/dynamic-table';
import { TieredMenu } from 'primereact/tieredmenu';
import lottie from 'lottie-web';
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../../Hooks/useAuth';
import { MODULE_NAMES } from '../../../Constants/modules';
import UseAxios from '../../../Api/http/http';
import emptyStateAnimationData from '../../../Assets/images/json/dar_no_data.json';
import PuzzleLoader from '../../../Assets/images/svg/loading puzzle.gif';
import {
  PROJECT_ALLOCATION,
  PROJECT_ALLOCATION_FILTERS,
} from '../../../Api/resources/endPoint';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { buildProjectAllocationApiUrl } from '../../../Api/request/projectAllocation';
import {
  getExportFileName,
  getExportFileNameWithDate,
} from '../../Delivery/utils';
import './ProjectAllocation.scss';

const getStoredAuthToken = () => localStorage.getItem('authToken') || '';

const projectAllocationBaseUrl = buildProjectAllocationApiUrl(PROJECT_ALLOCATION);
const HIDDEN_ALLOCATION_COLUMNS = new Set([
  'feallocation',
  'beallocation',
  'feallocationpct',
  'beallocationpct',
]);

const transformProjectAllocationExport = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;

  const rawTableConfig = Array.isArray(payload?.tableConfiguration)
    ? payload.tableConfiguration
    : (Array.isArray(payload?.data?.tableConfiguration) ? payload.data.tableConfiguration : []);
  const rawTableData = Array.isArray(payload?.tableData)
    ? payload.tableData
    : (Array.isArray(payload?.data?.tableData) ? payload.data.tableData : []);

  const sanitizedTableData = rawTableData.map((row) => {
    const updatedRow = { ...row };
    if (typeof updatedRow.projectName === 'string') {
      updatedRow.projectName = updatedRow.projectName.trim();
    }
    if (updatedRow.projectId !== undefined && updatedRow.projectId !== null && updatedRow.projectId !== '') {
      const pNum = Number(updatedRow.projectId);
      if (!isNaN(pNum)) {
        updatedRow.projectId = pNum;
      }
    }
    return updatedRow;
  });

  // Fields that SHOULD show totals (numeric: true in column config)
  const NUMERIC_EFFORT_FIELDS_WITH_TOTAL = new Set([
    'loggedEffort',
    'unloggedEffort',
    'totalEffort',
  ]);

  // Fields that are numeric but should NOT show a total in the footer row
  const NUMERIC_EFFORT_FIELDS_NO_TOTAL = new Set([
    'expectedEffort',
    'percentLogged',
  ]);

  const cleanTableConfig = rawTableConfig.map((col) => {
    let updatedCol = { ...col };
    if (col.field === 'projectId') {
      return {
        ...updatedCol,
        visible: true,
        numeric: true,
        // '0;-0;;' format: positive=integer, negative=-integer, zero=blank, text=blank
        // This hides the 0 the library writes for blank total row cells
        excelFormat: '0;-0;;',
        align: 'right',
        headerAlign: 'right',
        exportAlign: 'right',
        alignToUse: 'right',
        textAlign: 'right',
        alignment: { horizontal: 'right' },
        headerAlignment: { horizontal: 'right' },
        exportAlignment: { horizontal: 'right' },
        exportHeaderAlignment: { horizontal: 'right' },
        style: { ...(updatedCol.style || {}), textAlign: 'right' },
        headerStyle: { ...(updatedCol.headerStyle || {}), textAlign: 'right' },
      };
    }
    if (col.field === 'empId') {
      return {
        ...updatedCol,
        type: 'text',
        align: 'right',
        headerAlign: 'right',
        exportAlign: 'right',
        alignToUse: 'right',
        textAlign: 'right',
        alignment: { horizontal: 'right' },
        headerAlignment: { horizontal: 'right' },
        exportAlignment: { horizontal: 'right' },
        exportHeaderAlignment: { horizontal: 'right' },
        style: { ...(updatedCol.style || {}), textAlign: 'right' },
        headerStyle: { ...(updatedCol.headerStyle || {}), textAlign: 'right' },
      };
    }
    if (NUMERIC_EFFORT_FIELDS_WITH_TOTAL.has(col.field)) {
      updatedCol = {
        ...updatedCol,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        alignment: { horizontal: 'right' },
        headerAlignment: { horizontal: 'right' },
        exportAlign: 'right',
        numeric: true,
        style: { ...(updatedCol.style || {}), textAlign: 'right' },
        headerStyle: { ...(updatedCol.headerStyle || {}), textAlign: 'right' },
      };
    }
    if (NUMERIC_EFFORT_FIELDS_NO_TOTAL.has(col.field)) {
      // '0.00;-0.00;;' format: zero=blank so total row empty cell shows blank
      updatedCol = {
        ...updatedCol,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        alignment: { horizontal: 'right' },
        headerAlignment: { horizontal: 'right' },
        exportAlign: 'right',
        numeric: true,
        excelFormat: '0.00;-0.00;;',
        style: { ...(updatedCol.style || {}), textAlign: 'right' },
        headerStyle: { ...(updatedCol.headerStyle || {}), textAlign: 'right' },
      };
    }
    return updatedCol;
  });

  return {
    ...payload,
    tableConfiguration: cleanTableConfig,
    tableData: sanitizedTableData,
    ...(payload?.data ? { data: { ...payload.data, tableConfiguration: cleanTableConfig, tableData: sanitizedTableData } } : {}),
  };
};

const isHiddenAllocationColumn = (column = {}) => {
  const normalizedField = String(column.field || '').replace(/[^a-zA-Z]/g, '')
    .toLowerCase();
  const normalizedHeader = String(column.header || '').replace(/[^a-zA-Z]/g, '')
    .toLowerCase();
  return HIDDEN_ALLOCATION_COLUMNS.has(normalizedField)
    || HIDDEN_ALLOCATION_COLUMNS.has(normalizedHeader);
};

// ─── Global Filter Column Builder ───────────────────────────────────────────

const toFilterOption = (opt) => {
  if (opt && typeof opt === 'object') {
    const lbl = String(opt.name ?? opt.label ?? opt.value ?? '').trim();
    const val = String(opt.name ?? opt.value ?? opt.id ?? '').trim();
    return { id: val || lbl, name: lbl, label: lbl, value: val || lbl };
  }
  const strVal = String(opt ?? '').trim();
  return { id: strVal, name: strVal, label: strVal, value: strVal };
};

const buildGlobalFilterColumn = (field, label, options = [], template = 'multiSelect') => {
  const optionsMapped = (Array.isArray(options) ? options : [])
    .map(toFilterOption)
    .filter((o) => o.value !== '' && o.label !== '')
    .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true, sensitivity: 'base' }));

  return {
    field,
    header: label,
    width: 150,
    filter: true,
    visible: false,
    sortable: true,
    pinnable: false,
    defaultPinned: false,
    isGlobalFilter: true,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    menuItems: [
      {
        label,
        items: [
          {
            type: 'sort',
            field,
            serverSideSorting: true,
            valueType: 'string',
            options: [
              { label: 'Sort A-Z', value: 'ASC', order: 1 },
              { label: 'Sort Z-A', value: 'DESC', order: 2 },
            ],
          },
          { separator: true },
          {
            type: 'filter',
            field,
            label,
            placeholder: `Select ${label}`,
            template,
            selectedValues: [],
            selectedValue: null,
            value: [],
            options: optionsMapped,
            values: optionsMapped,
          },
        ],
      },
    ],
  };
};

const formatDateOnly = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentMonthDateRange = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return [formatDateOnly(firstDayOfMonth), formatDateOnly(lastDayOfMonth)];
};

const buildDateRangeGlobalFilterColumn = () => {
  const defaultRange = getCurrentMonthDateRange();

  return {
    field: 'dateRange',
    header: 'Date',
    width: 150,
    filter: true,
    visible: false,
    sortable: false,
    pinnable: false,
    defaultPinned: false,
    isGlobalFilter: true,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    menuItems: [
      {
        label: 'Date',
        items: [
          {
            type: 'filter',
            field: 'dateRange',
            label: 'Date',
            template: 'dateRange',
            placeholder: 'Select Date Range',
            range: defaultRange,
            selectedValues: defaultRange,
            value: defaultRange,
            isRequired: true,
            isMandatory: true,
            requiredOnMainApply: true,
            requiredMessage: 'Date range is a mandatory filter.',
            requiredValidationMessage: 'Please select both start date and end date before applying filters.',
            maxRangeDays: 31,
            maxRangeValidationMessage: 'Date range has to be within 31 days.',
            tableUtilityConfig: {
              requiredOnMainApply: true,
              requiredValidationMessage: 'Please select both start date and end date before applying filters.',
            },
          },
        ],
      },
    ],
  };
};

// ─── Access Control Helpers ──────────────────────────────────────────────────

/**
 * Extract standardized project identity (id and name) from various object formats
 * Handles different property naming conventions (camelCase, snake_case)
 */
const getProjectIdentity = (project) => {
  if (!project || typeof project !== 'object') return { id: '', name: '' };

  return {
    id: String(project.id ?? project.projectId ?? project.project_id ?? '').trim(),
    name: String(project.name ?? project.projectName ?? project.project_name ?? '')
      .trim()
      .toLowerCase(),
  };
};

/**
 * Check if a table row's project is in the user's accessible projects list
 * Returns true if accessibleProjects is empty/null (no restrictions)
 * Matches by both project ID and project name for flexibility
 */
const isProjectAccessible = (row, accessibleProjects) => {
  if (!Array.isArray(accessibleProjects) || accessibleProjects.length === 0) return true;

  const rowIdentity = getProjectIdentity({
    id: row?.projectId,
    name: row?.projectName,
  });

  return accessibleProjects.some((project) => {
    const projectIdentity = getProjectIdentity(project);
    return (rowIdentity.id && projectIdentity.id && rowIdentity.id === projectIdentity.id)
      || (rowIdentity.name && projectIdentity.name && rowIdentity.name === projectIdentity.name);
  });
};

// ─── Empty State Component ───────────────────────────────────────────────────

const EmptyState = ({ message }) => {
  const animationContainer = useRef(null);

  useEffect(() => {
    if (animationContainer.current) {
      const anim = lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: emptyStateAnimationData,
      });
      return () => anim.destroy();
    }
    return undefined;
  }, []);

  return (
    <div className="ms-empty-state-outer">
      <div className="ms-empty-state">
        <div className="ms-empty-state-animation-card">
          <div ref={animationContainer} className="ms-empty-state-animation" />
          <p className="ms-empty-state-text">{message}</p>
        </div>
      </div>
    </div>
  );
};

EmptyState.propTypes = { message: PropTypes.string.isRequired };

// ─── Toolbar Widget (Note + Tiered Menu Popup to EDR Reports) ────────────────

const ProjectAllocationToolbarWidget = () => {
  const menuRef = useRef(null);

  const menuItems = [
    {
      label: 'EDR Activity Report',
      command: () => window.open('/mis/edr-activity-report', '_blank'),
    },
    {
      label: 'EDR Project Effort',
      command: () => window.open('/mis/edr-project-effort', '_blank'),
    },
  ];

  return (
    <>
      <span className="project-allocation-note__text">
        <strong>Note:</strong> The efforts are in hours
      </span>
      <div className="tiered-menu-wrapper">
        <TieredMenu model={menuItems} popup ref={menuRef} />
        <button
          type="button"
          className="tiered-menu-btn"
          onClick={(e) => menuRef.current?.toggle(e)}
          title="More Reports"
        >
          <i className="pi pi-ellipsis-v" />
        </button>
      </div>
    </>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ProjectAllocation = () => {
  const { userProfile } = useAuth();
  const authToken = userProfile?.access_token || getStoredAuthToken();
  const permissions = useMemo(() => userProfile?.permissions || {}, [userProfile]);

  const hasPermission = useCallback(
    (moduleName, action) => {
      const actions = Array.isArray(permissions?.[moduleName]) ? permissions[moduleName] : [];
      return actions.includes(action);
    },
    [permissions],
  );

  const canView = useMemo(
    () => hasPermission(MODULE_NAMES.MIS_PROJECT_ALLOCATION, 'read'),
    [hasPermission],
  );

  const userInfo = useMemo(() => ({
    id: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    emailId: userProfile?.email || userProfile?.emailId || '',
    email: userProfile?.email || userProfile?.emailId || '',
    emp_id: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    empId: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    employeeId: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    employee_id: userProfile?.employeeId || userProfile?.emp_id || userProfile?.id || '',
    user_name: userProfile?.user_name || userProfile?.userName || '',
    permissions: userProfile?.permissions || {},
    roleDetails: Array.isArray(userProfile?.roleDetails) ? userProfile.roleDetails : [],
    role_details: Array.isArray(userProfile?.roleDetails) ? userProfile.roleDetails : [],
    loginType: userProfile?.login_type || userProfile?.loginType || userProfile?.Login_Type || '',
    login_type: userProfile?.login_type || userProfile?.loginType || userProfile?.Login_Type || '',
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [userProfile]);

  const [isLoading, setIsLoading] = useState(false);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    verticals: [],
    projects: [],
    statuses: [],
    types: [],
    employees: [],
    empIds: [],
    managers: [],
  });

  // ACCESS CONTROL: Store accessible projects separately to enforce permissions
  // This list comes from backend based on user's role and permissions
  // Used to filter both the Project Name dropdown and table data
  const [accessibleProjects, setAccessibleProjects] = useState([]);

  const tableContainerRef = useRef(null);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setIsLoading(true);
        const res = await UseAxios({
          url: PROJECT_ALLOCATION_FILTERS,
          method: 'post',
          data: { userInfo },
          payload: { userInfo },
          headers: { 'content-type': 'application/json' },
          authRequired: true,
          service: serviceName.PROJECT_MS,
        });

        if (res?.data?.data) {
          const fetchedProjects = res.data.data.projects || [];

          // ACCESS CONTROL: Store accessible projects for filtering
          // Backend returns only projects user has permission to view
          setAccessibleProjects(fetchedProjects);

          setFilterOptions({
            verticals: res.data.data.verticals || [],
            projects: fetchedProjects,
            statuses: res.data.data.statuses || [],
            types: res.data.data.types || [],
            employees: res.data.data.employees || [],
            empIds: res.data.data.empIds || [],
            managers: res.data.data.managers || [],
          });
        }
      } catch (err) {
        toast.error('Error fetching filter options');
      } finally {
        setIsLoading(false);
        setFiltersLoaded(true);
      }
    };
    fetchFilterOptions();
  }, [userInfo]);

  // Format API response & compute numeric alignments (No Total row)
  const handleProjectAllocationApiResponse = useCallback((response) => {
    if (!response || typeof response !== 'object') return response;

    if (response.data?.success === false || response.success === false) {
      const errorMsg = response.data?.message || response.message || 'Error fetching data';
      toast.error(errorMsg);
      return response;
    }

    const resData = response.data?.data || response.data || {};
    const meta = response.data?.meta || response.meta || {};

    // Build global filter columns with access-controlled projects
    // ACCESS CONTROL: Project Name filter only shows projects user has access to
    const globalFilterCols = [
      buildGlobalFilterColumn('projectName', 'Project Name', filterOptions.projects),
      buildGlobalFilterColumn('vertical', 'Project Vertical', filterOptions.verticals),
      buildGlobalFilterColumn('projectType', 'Project Type', filterOptions.types),
      buildGlobalFilterColumn('projectStatus', 'Project Status', filterOptions.statuses),
      buildGlobalFilterColumn('empName', 'Employee Name', filterOptions.employees),
      buildDateRangeGlobalFilterColumn(),
    ];

    const rawDataCols = Array.isArray(resData.tableConfiguration)
      ? resData.tableConfiguration
      : Array.isArray(response.data?.tableConfiguration)
        ? response.data.tableConfiguration
        : [];

    const dataCols = rawDataCols.filter((col) => !isHiddenAllocationColumn(col)).map((col) => {
      const isDefaultPinned =
        col.defaultPinned === true ||
        col.pinned === true ||
        col.field === 'projectName';
      let updated = {
        ...col,
        isGlobalFilter: false,
        visible: col.visible !== false,
        pinnable: isDefaultPinned,
        defaultPinned: isDefaultPinned,
        showPinButtonInHeader: false,
      };

      const matchingGlobalCol = globalFilterCols.find(g => g.field === col.field);
      if (matchingGlobalCol) {
        updated.isGlobalFilter = true;
        updated.menuItems = matchingGlobalCol.menuItems;
      }

      if (col.field === 'projectManager') {
        updated = {
          ...updated,
          width: 170,
          className: 'manager-cell',
          bodyClassName: 'manager-cell',
          style: {
            ...(updated.style || {}),
            maxWidth: '240px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            display: 'block',
          },
          headerStyle: {
            ...(updated.headerStyle || {}),
            maxWidth: '240px',
          },
        };
      }
      if (col.field === 'percentLogged') {
        updated = {
          ...updated,
          width: 130,
        };
      }
      return updated;
    });

    // Build combined table configuration with global filters in exact requested order:
    // 1. Project Name, 2. Project Vertical, 3. Project Type, 4. Project Status, 5. Employee Name, 6. Date
    const globalFilterMap = new Map(globalFilterCols.map((col) => [col.field, col]));
    const dataColsMap = new Map(dataCols.map((col) => [col.field, col]));

    const ORDERED_FIELDS = [
      'projectId',
      'projectName',
      'vertical',
      'projectType',
      'projectStatus',
      'projectManager',
      'empId',
      'empName',
      'empVertical',
      'dateRange',
      'loggedEffort',
      'unloggedEffort',
      'totalEffort',
      'expectedEffort',
      'percentLogged',
    ];

    const finalTableConfiguration = ORDERED_FIELDS.map((field) => {
      if (dataColsMap.has(field)) return dataColsMap.get(field);
      if (globalFilterMap.has(field)) return globalFilterMap.get(field);
      return null;
    }).filter(Boolean);

    dataCols.forEach((col) => {
      if (!finalTableConfiguration.some((c) => c.field === col.field)) {
        finalTableConfiguration.push(col);
      }
    });

    const rawTableData = Array.isArray(resData.tableData)
      ? resData.tableData
      : Array.isArray(response.data?.tableData)
        ? response.data.tableData
        : [];

    const grandTotals = resData.grandTotals || response.data?.grandTotals || response.grandTotals;

    // Remove any existing Total rows to prevent duplication
    const isExistingTotalRow = (row) => {
      const projectName = String(row?.projectName || '')
        .trim()
        .toLowerCase();
      return row?._isFooterRow === true ||
             row?.rowConfiguration?.isFrozen === true ||
             projectName === 'total';
    };

    // ACCESS CONTROL: Filter table data to show only accessible projects
    // This ensures users only see data for projects they have permission to view
    const accessScopedTableData = rawTableData.filter((row) => (
      isProjectAccessible(row, accessibleProjects)
    ));
    const sanitizedTableData = accessScopedTableData.filter((row) => !isExistingTotalRow(row));

    // Add Total row at the end (after sorting has been applied by backend)
    let tableData = [...sanitizedTableData];
    if (sanitizedTableData.length > 0 && grandTotals) {
      const totalRow = {
        projectName: 'Total',
        projectId: '',
        vertical: '',
        projectVertical: '',
        projectType: '',
        projectStatus: '',
        projectManager: '',
        empId: '',
        empName: '',
        empVertical: '',
        loggedEffort: grandTotals.totalLoggedEffort ?? 0,
        unloggedEffort: grandTotals.totalUnloggedEffort ?? 0,
        totalEffort: grandTotals.totalEffort ?? 0,
        expectedEffort: '',
        percentLogged: '',
        rowConfiguration: {
          isFrozen: true,
          template: [
            { field: 'projectName', fontWeight: '700' },
            { field: 'loggedEffort', fontWeight: '700' },
            { field: 'unloggedEffort', fontWeight: '700' },
            { field: 'totalEffort', fontWeight: '700' },
          ],
        },
        _isFooterRow: true,
      };
      tableData = [...sanitizedTableData, totalRow];
    }

    const totalRecords = meta.total ?? meta.count ?? sanitizedTableData.length;

    response.data = {
      tableConfiguration: finalTableConfiguration,
      tableData,
      grandTotals,
      data: {
        tableConfiguration: finalTableConfiguration,
        tableData,
        grandTotals,
      },
      meta: {
        page: meta.page || 1,
        limit: meta.limit || 10,
        total: totalRecords,
      },
    };

    response.meta = {
      page: meta.page || 1,
      limit: meta.limit || 10,
      total: totalRecords,
    };

    return response;
  }, [filterOptions, accessibleProjects]);

  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        enableApiSorting: true,
        autoFetch: true,
        baseURL: projectAllocationBaseUrl,
        showToolBar: true,
        showPagination: false,
        showTieredMenu: false,
        exportHeaderUnmerge: true,
        userInfo,
        headers: { token: authToken },
        additionalPayloadData: {
          userInfo,
          employeeId: userProfile?.employeeId || userProfile?.emp_id || '',
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: getExportFileName('Project_Allocation_Report'),
            sheetName: getExportFileNameWithDate('Project Allocation Report'),
            transformResponse: (payload) => transformProjectAllocationExport(payload),
          },
        },
        handler: {
          handleFilteredApiResponse: handleProjectAllocationApiResponse,
        },
        toolBarSettings: {
          showAddNew: false,
          showDownloadExcel: true,
          showColumnCustomize: false,
          showColumnFilterMenu: true,
          showHelp: false,
          showSearch: false,
          showToolBarHeading: true,
          toolBarHeading: 'Project Allocation %',
          additionalElements: <ProjectAllocationToolbarWidget />,
          downloadExcelFileName: getExportFileName('Project_Allocation_Report'),
          downloadExcelSheetName: getExportFileNameWithDate('Project Allocation Report'),
          showSaveView: false,
          showReset: false,
          showExpandCollapse: false,
          showFilterOption: true,
          showFilters: false,
        },
      },
      tableSettings: {
        size: 'small',
        tableScrollHeight: 'flex',
        loaderSkeleton: { rows: 10, bodyCells: 2, columns: 5 },
        showExpandCollapse: false,
      },
    }),
    [userInfo, authToken, userProfile, handleProjectAllocationApiResponse],
  );

  if (!canView) return <Navigate to="/home" replace />;

  return (
    <div className="project-allocation-outer" style={{ height: 'calc(100vh - 90px)', overflow: 'hidden', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      {isLoading && (
        <div className="loader-container">
          <img src={PuzzleLoader} alt="Loading..." style={{ width: '100px', height: 'auto' }} />
        </div>
      )}

      {/* Table Utility Container */}
      <div ref={tableContainerRef} className="project-allocation-table-wrapper" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {filtersLoaded && (
          <TableUtility configuration={configuration} showFilterOption />
        )}
      </div>
    </div>
  );
};

export default ProjectAllocation;
